import React from 'react';
import MilanLogo from './icons/MilanLogo';

/**
 * Renders Milan's responsive public download landing page.
 *
 * @returns {React.ReactElement} Download options and platform onboarding instructions.
 * @sideEffects Has no external side effects beyond React and Router rendering.
 */
export default function DownloadPage() {
  return (
    <>
      <div className="brand-top-fade brand-top-fade--visible" aria-hidden="true" />
      <div
        className="brand-mark brand-mark--compact"
        role="img"
        aria-label="Milan"
      >
        <MilanLogo className="brand-mark__logo" aria-hidden="true" />
      </div>

      <div className="bg-background min-h-screen">
        <div className="max-w-[800px] mx-auto px-5 sm:px-6 md:px-8 pt-28 pb-20 sm:pt-40 sm:pb-32">
          {/* Header section */}
          <div className="reveal mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.020em] leading-[1.1] mb-6 text-foreground">
              Try the Milan Beta
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Milan is currently in private beta on iOS and Android. Experience a relationship space built on reflection.
            </p>
          </div>

          {/* Download cards */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-10">
            {/* iOS TestFlight Card */}
            <div className="reveal bg-secondary/30 rounded-2xl p-8 border border-border/80 flex flex-col justify-between items-center text-center transition-all duration-200 hover:border-accent/40">
              <div className="mb-8 flex flex-col items-center">
                {/* SVG Apple Store Icon */}
                <svg viewBox="0 0 24 24" className="w-12 h-12 text-foreground mb-4" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.1,16.67C20.08,16.74 19.67,18.11 18.71,19.5M15.97,4.17C16.63,3.37 17.07,2.28 16.95,1C16,1.04 14.9,1.6 14.24,2.38C13.68,3.04 13.19,4.14 13.34,5.39C14.39,5.47 15.4,4.88 15.97,4.17Z" />
                </svg>
                <h3 className="text-2xl font-semibold text-foreground mb-2">iOS Beta</h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Join using Apple TestFlight to test on iPhone.
                </p>
              </div>
              <a
                href="https://testflight.apple.com/join/xxzMBJP2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center px-6 py-4 bg-primary text-primary-foreground hover:bg-accent hover:text-white text-base font-semibold rounded-xl transition-all duration-200 shadow-sm"
              >
                Download iOS Beta
              </a>
            </div>

            {/* Android Closed Testing Card */}
            <div className="reveal bg-secondary/30 rounded-2xl p-8 border border-border/80 flex flex-col justify-between items-center text-center transition-all duration-200 hover:border-accent/40">
              <div className="mb-8 flex flex-col items-center">
                {/* SVG Play Store Icon */}
                <svg viewBox="0 0 24 24" className="w-12 h-12 text-foreground mb-4" fill="currentColor">
                  <path d="m21.762,9.942L4.67.378c-.721-.466-1.635-.504-2.393-.099-.768.411-1.246,1.208-1.246,2.08v19.282c0,.872.477,1.668,1.246,2.079.755.404,1.668.37,2.393-.098l17.092-9.564c.756-.423,1.207-1.192,1.207-2.058s-.451-1.635-1.207-2.058Zm-5.746-1.413l-2.36,2.36L5.302,2.534l10.714,5.995ZM2.604,21.906V2.094l9.941,9.906L2.604,21.906Zm2.698-.439l8.355-8.355,2.36,2.36-10.714,5.995Zm15.692-8.78l-3.552,1.987-2.674-2.674,2.674-2.674,3.552,1.987c.363.203.402.548.402.686s-.039.483-.402.686Z" />
                </svg>
                <h3 className="text-2xl font-semibold text-foreground mb-2">Android Beta</h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Join via Google Play Closed Testing.
                </p>
              </div>
              <a
                href="https://play.google.com/apps/testing/com.milan.dating"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center px-6 py-4 bg-primary text-primary-foreground hover:bg-accent hover:text-white text-base font-semibold rounded-xl transition-all duration-200 shadow-sm"
              >
                Join Android Beta
              </a>
            </div>
          </div>

          {/* Android Helper Info Card */}
          <div className="reveal bg-secondary/20 rounded-2xl p-6 border border-border/60 mb-10 text-left">
            <h4 className="text-lg font-semibold text-foreground mb-3">Android Opt-In Instructions:</h4>
            <p className="text-base text-muted-foreground leading-relaxed">
              Google Play closed testing requires your Google Play account email to be authorized. Please make sure your email is added to the testers list. If you see a "Not Found" error, contact me to get added.
            </p>
          </div>


        </div>
      </div>
    </>
  );
}
